import React, { useState, useMemo } from "react";
import classNames from "classnames";
import {
    Form,
    FormGroup,
    Input,
    Row,
    Col,
    Label,
    Badge,
    Button,
    Spinner,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useNavigate } from "react-router-dom";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { resolveDealListReceivedDate } from "../../../utils/dealListDates";
import { useTranslation } from 'react-i18next';
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import { useGetDeals } from "api/deals/service";
import {
    getTheme2MultiSelectCss,
    getTheme2MultiSelectSelectConfig,
    getTheme2SearchFilterColProps,
    getTheme2SearchFilterFieldCss,
    getTheme2SearchFilterMultiSelectStyles,
    getTheme2SearchFilterSingleSelectStyles,
    getTheme2SearchFilterStatusFieldCss,
} from "utils/theme2MultiSelect";

const SA_DEALS_STATUS_PREFIX = "sa-deals-status";

const SA_DEALS_TAB_STYLES = (isTheme2) => `
  ${isTheme2 ? `
  ${getTheme2SearchFilterFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-deals-tab",
        filtersSelector: ".sa-deals-filters",
        rowClass: "sa-deals-filters-row",
        colClass: "sa-deals-filter-col",
    })}
  ${getTheme2SearchFilterStatusFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2",
        filterColClass: "sa-deals-filter-col",
    })}
  ${getTheme2MultiSelectCss({
        prefix: SA_DEALS_STATUS_PREFIX,
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-deals-tab",
    })}
  ` : ""}
`;

const SADealsTab = ({ activeColor = "blue", isTheme2 = false }) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const navigate = useNavigate();

    const statusMultiSelectStyles = useMemo(
        () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const filterSelectStyles = useMemo(
        () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const statusTheme2Config = useMemo(
        () => getTheme2MultiSelectSelectConfig(SA_DEALS_STATUS_PREFIX, isTheme2),
        [isTheme2]
    );
    const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
        filterColClass: "sa-deals-filter-col",
        md: "4",
    });
    const statusFilterColProps = isTheme2
        ? {
            ...filterColProps,
            className: `${filterColProps.className} sa-deals-filter-col--status`,
        }
        : filterColProps;

    const statusOptions = useMemo(() => [
        { value: "working", label: t('searchDeals.working') },
        { value: "prospect", label: t('searchDeals.prospect') },
        { value: "contracted", label: "Contracted" },
        { value: "spot_delivered", label: "Spot Delivered" },
        { value: "cancelled", label: "Cancelled" },
        { value: "contracted_booked", label: "Contracted/Booked" },
    ], [t]);

    const dealTypeOptions = useMemo(() => [
        { value: "", label: t('searchDeals.selectDealType') },
        { value: t('searchDeals.retail'), label: t('searchDeals.retail') },
        { value: t('searchDeals.wholesale'), label: t('searchDeals.wholesale') },
        { value: t('searchDeals.trade'), label: t('searchDeals.trade') },
    ], [t]);

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState([]);
    const [dealType, setDealType] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [expandedRows, setExpandedRows] = useState({});

    const formatDisplayText = (text) => {
        if (!text) return '';
        return text
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const statusArray = useMemo(
        () => status.map((option) => option.value),
        [status]
    );

    const DealsTable = isTheme2 ? ReactTable2 : ReactTable;
    const { data: deals, isLoading } = useGetDeals(search, statusArray, page, pageSize);

    const dealColumns = useMemo(() => [
        {
            Header: t('searchDeals.dealId'),
            accessor: "deal_id",
            width: 80,
            minWidth: 80,
            maxWidth: 80,
        },
        {
            Header: t('searchDeals.receivedDate'),
            accessor: "received_date",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
        },
        {
            Header: t('searchDeals.applicantDetails'),
            accessor: "applicant_details",
            className: "applicant-details-column",
            minWidth: 500,
            width: 500,
            maxWidth: 500,
            Cell: ({ value, row }) => {
                const rowId = row.id;
                const isExpanded = expandedRows[rowId];
                const worksheets = value?.worksheets || [];
                const hasMoreThan1 = worksheets.length > 1;

                return (
                    <div>
                        {value?.name && (
                            <div style={{ fontWeight: "600", fontSize: "13px", marginBottom: "6px", color: "#1a6fbf" }}>
                                {value.name}
                            </div>
                        )}
                        {worksheets.slice(0, isExpanded ? worksheets.length : Math.min(1, worksheets.length)).map((worksheet) => (
                            <div
                                className="worksheet-container"
                                style={{
                                    display: "flex",
                                    alignItems: "flex-end",
                                    border: "0.5px solid",
                                    padding: "10px",
                                    justifyContent: "space-between",
                                    marginBottom: "10px",
                                    width: "100%"
                                }}
                                key={worksheet.worksheet_id}
                            >
                                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{
                                        fontWeight: "bold",
                                        fontSize: "13px"
                                    }}>
                                        {worksheet.type === "local_lender_finance" ? (worksheet?.finance_company?.name || worksheet?.local_lender?.name) : worksheet.type === "cash" ? t('searchDeals.cash') : worksheet.type === "wholesale" ? t('searchDeals.wholesale') : t('searchDeals.inHouse')}
                                    </div>
                                    <div style={{ marginLeft: "10px", textAlign: "left" }}>
                                        <div style={{ fontSize: "13px" }}>
                                            {worksheet.inventory?.inventory_vehicle?.year} {worksheet.inventory?.inventory_vehicle?.make} {worksheet.inventory?.inventory_vehicle?.model}
                                        </div>
                                        <div style={{ fontWeight: "bold", fontSize: "12px" }}>{worksheet.contract_date ? "Contract Date:" : ""}</div>
                                        <div style={{ fontSize: "12px" }}>{formatDateForDisplay(worksheet.contract_date || "-")}</div>
                                        <div style={{ fontSize: "12px" }}>{worksheet.working_date ? "Working Date:" : ""}</div>
                                        <div style={{ fontSize: "12px" }}>{formatDateForDisplay(worksheet.working_date || "-")}</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: "right", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end", height: "100%", gap: "4px" }}>
                                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#1a6fbf" }}>
                                        {formatDisplayText(worksheet.worksheet_status)}
                                    </span>
                                    <div style={{ fontSize: "11px", opacity: 0.7 }}>
                                        {formatDateForDisplay(worksheet?.status_change_date)}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {hasMoreThan1 && (
                            <div style={{ position: "relative", zIndex: 1000 }}>
                                <Button
                                    style={{ position: "relative", zIndex: 1001, background: "transparent", border: "none" }}
                                    color="link"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedRows(prev => ({
                                            ...prev,
                                            [rowId]: !prev[rowId]
                                        }));
                                    }}
                                >
                                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                </Button>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            Header: t('searchDeals.createdBy'),
            accessor: "created_user_full_name",
            width: 150,
            minWidth: 150,
            maxWidth: 150,
        },
        {
            Header: () => (
                <div style={{ textAlign: "center", width: "100%" }}>
                    {t('searchDeals.status')} / {t('searchDeals.flag')}
                </div>
            ),
            accessor: "status_flag",
            width: 200,
            minWidth: 200,
            maxWidth: 200,
            Cell: ({ value }) => (
                <div style={{ textAlign: "center", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                    {value?.status}
                    {value?.flag}
                </div>
            )
        },
    ], [t, expandedRows]);

    const dealData = useMemo(() => {
        const list = deals?.results || deals || [];
        return list.map((item) => ({
            id: item.deal_id,
            deal_id: item.deal_id || "",
            received_date: formatDateForDisplay(resolveDealListReceivedDate(item)),
            applicant_details: { name: item?.full_name || item?.wholesaler_name || "", worksheets: item?.worksheets || [] },
            created_user_full_name: item.created_user_full_name || "",
            status_flag: {
                status: <Badge color={item.deal_status === "working" ? "success" : item.deal_status === "prospect" ? "info" : item.deal_status === "completed" ? "success" : "danger"}>{formatDisplayText(item.deal_status)}</Badge>,
                flag: item.is_have_flags ? <Badge color="success">{t('searchDeals.yes')}</Badge> : <Badge color="danger">{t('searchDeals.no')}</Badge>,

            },
        }));
    }, [deals, t]);

    return (
        <div
            className={classNames("sa-deals-tab", {
                "sa-deals-tab--theme2": isTheme2,
            })}
        >
            <style>{SA_DEALS_TAB_STYLES(isTheme2)}</style>
            <div className="sa-deals-filters">
                <Form>
                    <Row className={isTheme2 ? "sa-deals-filters-row" : undefined}>
                        <Col {...filterColProps}>
                            <FormGroup>
                                <Label>{t('searchDeals.search')}:</Label>
                                <Input
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder={t('searchDeals.searchPlaceholder')}
                                    className="form-control"
                                />
                            </FormGroup>
                        </Col>
                        <Col {...statusFilterColProps}>
                            <FormGroup>
                                <Label>{t('searchDeals.status')}:</Label>
                                <Select
                                    activeColor={activeColor}
                                    className={statusTheme2Config.controlClassName}
                                    placeholder={t('searchDeals.selectStatus')}
                                    name="status"
                                    isSearchable={false}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={statusTheme2Config.hideSelectedOptions}
                                    value={status}
                                    onChange={(option) => setStatus(option || [])}
                                    options={statusOptions}
                                    isClearable
                                    styles={statusMultiSelectStyles}
                                    components={statusTheme2Config.components}
                                    classNames={statusTheme2Config.classNames}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...filterColProps}>
                            <FormGroup>
                                <Label>{t('searchDeals.dealType')}:</Label>
                                <Select
                                    activeColor={activeColor}
                                    className="react-select"
                                    styles={filterSelectStyles}
                                    placeholder={t('searchDeals.selectDealType')}
                                    name="dealType"
                                    isSearchable={false}
                                    closeMenuOnSelect={true}
                                    value={
                                        dealType
                                            ? {
                                                value: dealType,
                                                label: dealTypeOptions.find((option) => option.value === dealType)?.label || dealType,
                                            }
                                            : null
                                    }
                                    onChange={(option) => setDealType(option?.value || "")}
                                    options={dealTypeOptions}
                                    isClearable
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div
                className={classNames("mt-2", { "inventory-table-wrap": isTheme2 })}
                style={isTheme2 ? { fontSize: "13px", minWidth: 0, maxWidth: "100%" } : undefined}
            >
                {isTheme2 ? (
                    <DealsTable
                        activeColor={activeColor}
                        data={dealData}
                        columns={dealColumns}
                        disableViewportClamp
                        className="-striped -highlight sticky-stock-table"
                        manualPagination={true}
                        pageCount={deals?.pagination?.pageCount || 1}
                        pageIndex={page - 1}
                        pageSize={pageSize}
                        totalItems={deals?.pagination?.total}
                        loading={isLoading}
                        loadingText={t("searchDeals.loading")}
                        noDataText={t("searchDeals.noRecordsFound")}
                        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        onRowClick={(row) => {
                            if (row?.deal_id) {
                                navigate(`/deals/${row.deal_id}`);
                            }
                        }}
                    />
                ) : isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        <Spinner color={activeColorStyle.borderColor} />
                    </div>
                ) : (
                    <DealsTable
                        activeColor={activeColor}
                        data={dealData}
                        columns={dealColumns}
                        className="-striped -highlight"
                        manualPagination={true}
                        pageCount={deals?.pagination?.pageCount || 1}
                        pageIndex={page - 1}
                        pageSize={pageSize}
                        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        onRowClick={(row) => {
                            if (row?.deal_id) {
                                navigate(`/deals/${row.deal_id}`);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SADealsTab;
